import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../api";
import { useInputChangeHandler } from "../../hooks/useInputChangeHandler";
import { useLoginApi } from "../../hooks/useLoginApi";
import { useStoredApiToken } from "../../store/apiToken";
import classes from "./Login.module.css";

export const Login = () => {
  const { api } = useApi();
  const { t } = useTranslation();
  const titleId = useId();
  const [, setApiToken] = useStoredApiToken();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const handlePasswordChange = useInputChangeHandler(setPassword);
  const handleUsernameChange = useInputChangeHandler(setUsername);

  const { error, loading, login, resetLogin, token } = useLoginApi(api);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();

      login({
        email: username,
        password,
      });
    },
    [login, password, username],
  );

  useEffect(() => {
    if (typeof token !== "undefined") {
      setApiToken(token);

      resetLogin();
    }
  }, [resetLogin, setApiToken, token]);

  const realLoading = typeof loading !== "undefined" ? loading : false;
  const inputsDisabled = realLoading;
  const isCredentialsError = error === "credentials";

  return (
    <div className={classes.root}>
      <Paper
        aria-labelledby={titleId}
        className={classes.form}
        component="form"
        onSubmit={handleSubmit}
      >
        <Typography component="h1" id={titleId} variant="h6">
          {t("Login.title")}
        </Typography>
        <Typography color="error" variant="subtitle2">
          {typeof error !== "undefined" && t(`Login.error.${error}`)}
        </Typography>
        <TextField
          autoComplete="username"
          autoFocus
          disabled={inputsDisabled}
          error={isCredentialsError}
          fullWidth
          label={t("Login.username")}
          margin="normal"
          onChange={handleUsernameChange}
          type="email"
          value={username}
        />
        <TextField
          autoComplete="current-password"
          disabled={inputsDisabled}
          error={isCredentialsError}
          fullWidth
          label={t("Login.password")}
          margin="normal"
          onChange={handlePasswordChange}
          type="password"
          value={password}
        />
        <div className={classes.actions}>
          <Button color="primary" loading={realLoading} type="submit">
            {t("Login.login")}
          </Button>
        </div>
      </Paper>
    </div>
  );
};
